import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase'; // Adjust path if needed
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  orderBy
} from 'firebase/firestore';
import './Admin.css';

const ADMIN_USERNAME = 'admin';
const ADMIN_PASSWORD = 'password123';

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [products, setProducts] = useState([]);
  const [codes, setCodes] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingCodes, setIsLoadingCodes] = useState(false);
  const [error, setError] = useState('');

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editProductData, setEditProductData] = useState(null);

  // --- MODIFIED: newCodeData state for customer type ---
  const [newCodeData, setNewCodeData] = useState({
    code: '',
    name: '',
    phone: '',
    discount: 0,
    interestedIn: '',
    customerType: '', // Will be 'domestic', 'export', or ''
  });
  // --- END MODIFICATION ---

  const [editCodeData, setEditCodeData] = useState(null);
  const [editingCodeId, setEditingCodeId] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
      fetchCodes();
    }
  }, [isAuthenticated]);

  const fetchProducts = useCallback(async () => {
    setIsLoadingProducts(true);
    setError('');
    try {
      const q = query(collection(db, 'products'), orderBy('sno'));
      const querySnapshot = await getDocs(q);
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsData);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products.');
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    setIsLoadingCodes(true);
    setError('');
    try {
      const q = query(collection(db, 'codes'), orderBy('code'));
      const querySnapshot = await getDocs(q);
      const codesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCodes(codesData);
    } catch (err) {
      console.error('Error fetching codes:', err);
      setError('Failed to load customer codes.');
    } finally {
      setIsLoadingCodes(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setLoginError('');
      setUsername('');
      setPassword('');
    } else {
      setLoginError('Invalid username or password.');
    }
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setEditProductData(JSON.parse(JSON.stringify(product)));
  };

  const handleEditProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    const keys = name.split('.');
    setEditProductData(prev => {
      const newData = { ...prev };
      let currentLevel = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!currentLevel[keys[i]]) currentLevel[keys[i]] = {};
        currentLevel = currentLevel[keys[i]];
      }
      const finalKey = keys[keys.length - 1];
      let processedValue = value;
      if (type === 'number') processedValue = value === '' ? '' : Number(value);
      else if (type === 'checkbox') processedValue = checked;
      currentLevel[finalKey] = processedValue;
      return newData;
    });
  };

  const handleUpdateProduct = async (e) => {
     e.preventDefault();
     if (!selectedProduct || !editProductData) return;
     if (editProductData.Rate === '' || editProductData.SKU === '' || editProductData.L === '' || editProductData.B === '' || editProductData.H === '') {
        alert('Numeric fields like Rate, SKU, L, B, H cannot be empty.');
        return;
     }
     const productRef = doc(db, 'products', selectedProduct.id);
     try {
        setIsLoadingProducts(true);
        const { id, ...updateData } = editProductData;
        await updateDoc(productRef, updateData);
        alert('Product updated successfully!');
        setSelectedProduct(null);
        setEditProductData(null);
        fetchProducts();
     } catch (err) {
        console.error("Error updating product: ", err);
        alert('Failed to update product.');
        setIsLoadingProducts(false);
     }
   };

   const cancelEditProduct = () => {
      setSelectedProduct(null);
      setEditProductData(null);
   };

   const handleNewCodeChange = (e) => {
        const { name, value, type } = e.target;
        setNewCodeData(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value
        }));
   };

   // --- ADDED: Handler for new code's customer type radio buttons ---
   const handleNewCodeCustomerTypeChange = (e) => {
        setNewCodeData(prev => ({
            ...prev,
            customerType: e.target.value // This will be 'domestic', 'export', or ''
        }));
   };
   // --- END ADDITION ---

   const handleAddCode = async (e) => {
        e.preventDefault();
        if (!newCodeData.code || !newCodeData.name) {
            alert('Code and Name are required.');
            return;
        }
        // Optional: You might want to make customerType selection mandatory
        // if (newCodeData.customerType === '') {
        //     alert('Please select a customer type (Domestic or Export).');
        //     return;
        // }
        setIsLoadingCodes(true);
        try {
            const formattedInterestedIn = newCodeData.interestedIn
                ? newCodeData.interestedIn.split(',').map(sku => sku.trim()).filter(sku => sku).join(',')
                : '';

            // --- MODIFIED: Prepare data for Firestore with isDomestic/isExport ---
            const codeDataToSave = {
                code: newCodeData.code,
                name: newCodeData.name,
                phone: newCodeData.phone,
                discount: Number(newCodeData.discount) || 0,
                interestedIn: formattedInterestedIn,
                isDomestic: newCodeData.customerType === 'domestic',
                isExport: newCodeData.customerType === 'export',
            };
            // --- END MODIFICATION ---

            await addDoc(collection(db, 'codes'), codeDataToSave);
            alert('Code added successfully!');
            setNewCodeData({ // Reset form including customerType
                code: '',
                name: '',
                phone: '',
                discount: 0,
                interestedIn: '',
                customerType: ''
            });
            fetchCodes();
        } catch (err) {
            console.error("Error adding code: ", err);
            alert('Failed to add code.');
        } finally {
            setIsLoadingCodes(false);
        }
   };

   const handleEditCode = (code) => {
      setEditingCodeId(code.id);
      // --- MODIFIED: Initialize editCodeData with customerType ---
      let currentCustomerType = '';
      if (code.isDomestic) {
        currentCustomerType = 'domestic';
      } else if (code.isExport) {
        currentCustomerType = 'export';
      }
      setEditCodeData({ ...code, customerType: currentCustomerType });
      // --- END MODIFICATION ---
   };

   const handleEditCodeChange = (e) => {
      const { name, value, type } = e.target;
      setEditCodeData(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value
        }));
   };

   // --- ADDED: Handler for edited code's customer type radio buttons ---
   const handleEditCodeCustomerTypeChange = (e) => {
        setEditCodeData(prev => ({
            ...prev,
            customerType: e.target.value
        }));
   };
   // --- END ADDITION ---

   const handleUpdateCode = async (codeId) => {
      if (!editCodeData) return;
      // Optional: Validate editCodeData.customerType if selection is mandatory
      // if (editCodeData.customerType === '') {
      //     alert('Please select a customer type (Domestic or Export) for the code being edited.');
      //     return;
      // }
      const codeRef = doc(db, 'codes', codeId);
      try {
        setIsLoadingCodes(true);
        // Destructure to separate local customerType from other fields
        const { id, customerType, ...fieldsToUpdate } = editCodeData;

        // --- MODIFIED: Prepare data for Firestore update ---
        const dataToUpdateInFirestore = {
            ...fieldsToUpdate,
            discount: Number(fieldsToUpdate.discount) || 0,
            isDomestic: editCodeData.customerType === 'domestic',
            isExport: editCodeData.customerType === 'export',
        };
        // --- END MODIFICATION ---

        await updateDoc(codeRef, dataToUpdateInFirestore);
        alert('Code updated successfully!');
        setEditingCodeId(null);
        setEditCodeData(null);
        fetchCodes();
      } catch (err) {
         console.error("Error updating code: ", err);
         alert('Failed to update code.');
      } finally {
         setIsLoadingCodes(false);
      }
   };

   const cancelEditCode = () => {
      setEditingCodeId(null);
      setEditCodeData(null);
   };

   const handleDeleteCode = async (codeId) => {
        if (!window.confirm('Are you sure you want to delete this code?')) return;
        const codeRef = doc(db, 'codes', codeId);
        try {
            setIsLoadingCodes(true);
            await deleteDoc(codeRef);
            alert('Code deleted successfully!');
            fetchCodes();
        } catch (err) {
            console.error("Error deleting code: ", err);
            alert('Failed to delete code.');
        } finally {
            setIsLoadingCodes(false);
        }
   };

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <h2>Admin Login</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group"><label htmlFor="username">Username</label><input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required /></div>
          <div className="form-group"><label htmlFor="password">Password</label><input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          {loginError && <p className="error-message">{loginError}</p>}
          <button type="submit" className="btn btn-primary">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>
      {error && <p className="error-message">{error}</p>}

      <section className="admin-section">
        <h2>Product Management</h2>
        {isLoadingProducts && <p>Loading products...</p>}
        {selectedProduct && editProductData && (
          <div className="edit-form-container product-edit-form">
            <h3>Editing: {selectedProduct["Product name"]} (SKU: {selectedProduct.SKU})</h3>
            <form onSubmit={handleUpdateProduct}>
              <div className="form-grid">
                  <div className="form-group"><label>Product Name</label><input type="text" name="Product name" value={editProductData["Product name"]} onChange={handleEditProductChange} required/></div>
                  <div className="form-group"><label>SKU</label><input type="number" name="SKU" value={editProductData.SKU} onChange={handleEditProductChange} required/></div>
                  <div className="form-group"><label>sno</label><input type="number" name="sno" value={editProductData.sno} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Mold #</label><input type="number" name="mold" value={editProductData.mold} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Rate (Base)</label><input type="number" step="0.01" name="Rate" value={editProductData.Rate} onChange={handleEditProductChange} required/></div>
                  <div className="form-group"><label>Rate 2</label><input type="number" step="0.01" name="rate2" value={editProductData.rate2} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Rate 3</label><input type="number" step="0.01" name="rate3" value={editProductData.rate3} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Length (L)</label><input type="number" step="0.01" name="L" value={editProductData.L} onChange={handleEditProductChange} required/></div>
                  <div className="form-group"><label>Breadth (B)</label><input type="number" step="0.01" name="B" value={editProductData.B} onChange={handleEditProductChange} required/></div>
                  <div className="form-group"><label>Height (H)</label><input type="number" step="0.01" name="H" value={editProductData.H} onChange={handleEditProductChange} required/></div>
                  <div className="form-group full-width"><label>AKA</label><textarea name="aka" value={editProductData.aka || ''} onChange={handleEditProductChange} rows="3"></textarea></div>
                  <div className="form-group full-width"><label>Description</label><textarea name="description" value={editProductData.description || ''} onChange={handleEditProductChange} rows="4"></textarea></div>
                  <div className="form-group full-width"><label>Image (Main URL)</label><input type="url" name="image" value={editProductData.image || ''} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Image 2 URL</label><input type="url" name="subimages.image2" value={editProductData.subimages?.image2 || ''} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Image 3 URL</label><input type="url" name="subimages.image3" value={editProductData.subimages?.image3 || ''} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Image 4 URL</label><input type="url" name="subimages.image4" value={editProductData.subimages?.image4 || ''} onChange={handleEditProductChange} /></div>
                  <div className="form-group"><label>Image 5 URL</label><input type="url" name="subimages.image5" value={editProductData.subimages?.image5 || ''} onChange={handleEditProductChange} /></div>
                  <div className="form-group checkbox-group"><label htmlFor="isTrendingEdit">Is Trending?</label><input type="checkbox" id="isTrendingEdit" name="isTrending" checked={!!editProductData.isTrending} onChange={handleEditProductChange} /></div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-success" disabled={isLoadingProducts}>{isLoadingProducts ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" className="btn btn-secondary" onClick={cancelEditProduct} disabled={isLoadingProducts}>Cancel</button>
              </div>
            </form>
          </div>
        )}
        {!selectedProduct && (
            <div className="item-list product-list">
                {products.map(product => (
                <div className="list-item product-item" key={product.id}>
                    <img src={product.image || './placeholder.png'} alt={product["Product name"]} className="item-thumbnail" onError={(e) => e.target.src='./placeholder.png'}/>
                    <div className="item-info">
                        <span className="item-name">{product["Product name"]}</span>
                        <span className="item-detail">SKU: {product.SKU}</span>
                        <span className="item-detail">Rate: ₹{product.Rate?.toFixed(2)}</span>
                    </div>
                    <div className="item-actions"><button onClick={() => handleEditProduct(product)} className="btn btn-edit">Edit</button></div>
                </div>
                ))}
            </div>
        )}
      </section>

      <section className="admin-section">
        <h2>Customer Codes</h2>
        <div className="add-form-container code-add-form">
             <h3>Add New Code</h3>
             <form onSubmit={handleAddCode}>
                <div className="form-group"><input type="text" name="code" placeholder="Customer code" value={newCodeData.code} onChange={handleNewCodeChange} required maxLength="4"/></div>
                <div className="form-group"><input type="text" name="name" placeholder="Customer/Reference Name" value={newCodeData.name} onChange={handleNewCodeChange} required/></div>
                <div className="form-group"><input type="tel" name="phone" placeholder="Phone (Optional)" value={newCodeData.phone} onChange={handleNewCodeChange} /></div>
                <div className="form-group"><input type="number" name="discount" placeholder="Discount % (e.g., 10)" value={newCodeData.discount} onChange={handleNewCodeChange} required min="0" max="100"/></div>
                <div className="form-group"><input type="text" name="interestedIn" placeholder="Interested SKUs (comma-separated)" value={newCodeData.interestedIn} onChange={handleNewCodeChange}/></div>

                {/* --- ADDED: Radio buttons for new code customer type --- */}
                <div className="form-group radio-group">
                    <label style={{ marginRight: '10px' }}>Type:</label>
                    <div>
                        <input type="radio" id="newCodeDomestic" name="newCustomerType" value="domestic"
                               checked={newCodeData.customerType === 'domestic'}
                               onChange={handleNewCodeCustomerTypeChange} />
                        <label htmlFor="newCodeDomestic" style={{ marginLeft: '5px', marginRight: '15px' }}>Domestic</label>
                    </div>
                    <div>
                        <input type="radio" id="newCodeExport" name="newCustomerType" value="export"
                               checked={newCodeData.customerType === 'export'}
                               onChange={handleNewCodeCustomerTypeChange} />
                        <label htmlFor="newCodeExport" style={{ marginLeft: '5px' }}>Export</label>
                    </div>
                    {/* Optional: Add a "None" or "Clear" option */}
                    {/*
                    <div>
                        <input type="radio" id="newCodeNone" name="newCustomerType" value=""
                               checked={newCodeData.customerType === ''}
                               onChange={handleNewCodeCustomerTypeChange} />
                        <label htmlFor="newCodeNone" style={{ marginLeft: '5px' }}>None</label>
                    </div>
                    */}
                </div>
                {/* --- END ADDITION --- */}

                <button type="submit" className="btn btn-primary" disabled={isLoadingCodes}>
                    {isLoadingCodes ? 'Adding...' : 'Add Code'}
                </button>
             </form>
        </div>

         {isLoadingCodes && <p>Loading codes...</p>}
        <div className="item-list code-list">
             <h3>Existing Codes</h3>
             {codes.length === 0 && !isLoadingCodes && <p>No codes found.</p>}
             {codes.map(code => (
                <div className="list-item code-item" key={code.id}>
                    {editingCodeId === code.id && editCodeData ? (
                        <div className='inline-edit-form'>
                            <input type="text" name="code" value={editCodeData.code} onChange={handleEditCodeChange} required/>
                            <input type="text" name="name" value={editCodeData.name} onChange={handleEditCodeChange} required/>
                            <input type="tel" name="phone" value={editCodeData.phone || ''} onChange={handleEditCodeChange} />
                            <input type="number" name="discount" value={editCodeData.discount || 0} onChange={handleEditCodeChange} required min="0" max="100"/>
                            <input type="text" name="interestedIn" value={editCodeData.interestedIn || ''} onChange={handleEditCodeChange}/>

                            {/* --- ADDED: Radio buttons for editing customer type --- */}
                            <div className="form-group radio-group inline-radio-group" style={{ marginTop: '8px', marginBottom: '8px' }}>
                                <label style={{ marginRight: '5px' }}>Type:</label>
                                <div>
                                    <input type="radio" id={`editCodeDomestic-${code.id}`} name={`editCustomerType-${code.id}`} value="domestic"
                                           checked={editCodeData.customerType === 'domestic'}
                                           onChange={handleEditCodeCustomerTypeChange} />
                                    <label htmlFor={`editCodeDomestic-${code.id}`} style={{ marginLeft: '3px', marginRight: '10px' }}>Domestic</label>
                                </div>
                                <div>
                                    <input type="radio" id={`editCodeExport-${code.id}`} name={`editCustomerType-${code.id}`} value="export"
                                           checked={editCodeData.customerType === 'export'}
                                           onChange={handleEditCodeCustomerTypeChange} />
                                    <label htmlFor={`editCodeExport-${code.id}`} style={{ marginLeft: '3px' }}>Export</label>
                                </div>
                            </div>
                            {/* --- END ADDITION --- */}

                            <button onClick={() => handleUpdateCode(code.id)} className='btn btn-save' disabled={isLoadingCodes}>Save</button>
                            <button onClick={cancelEditCode} className='btn btn-cancel' disabled={isLoadingCodes}>Cancel</button>
                        </div>
                    ) : (
                        <>
                            <div className="item-info">
                                <span className="item-name">Code: {code.code}</span>
                                <span className="item-detail">Name: {code.name}</span>
                                <span className="item-detail">Phone: {code.phone || 'N/A'}</span>
                                <span className="item-detail">Discount: {code.discount}%</span>
                                {/* --- ADDED: Display current code type --- */}
                                <span className="item-detail">
                                    Type: {code.isDomestic ? 'Domestic' : (code.isExport ? 'Export' : 'N/A')}
                                </span>
                                {/* --- END ADDITION --- */}
                                {code.interestedIn && <span className="item-detail">Interested SKUs: {code.interestedIn}</span>}
                            </div>
                            <div className="item-actions">
                                <button onClick={() => handleEditCode(code)} className="btn btn-edit">Edit</button>
                                <button onClick={() => handleDeleteCode(code.id)} className="btn btn-delete" disabled={isLoadingCodes}>Delete</button>
                            </div>
                        </>
                    )}
                </div>
             ))}
        </div>
      </section>
    </div>
  );
}

export default Admin;